import { useEffect } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import * as state from '@/components/Analysis/state';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import {
  GetStructureForInspectionDataLoaderQuery,
  GetStructureRelationshipsForAnalysisDataLoaderQuery,
} from '@/__generated__/graphql';
import { AnalysisStructureRelationships } from '@/types';
import { auth0TokenState } from '../../../../state';

const structureRelationshipsQueryText = `
  query getStructureRelationships($structureId: String!, $templateId: String) {
    allAnnotations(structureId: $structureId, templateId: $templateId) {
      id
      annotationId
      partId
      locationId
    }
    allParts(structureId: $structureId, templateId: $templateId) {
      id
      assemblyId
      class
    }
    allAssemblies(structureId: $structureId, templateId: $templateId) {
      id
      tagName
      integerId
    }
  }
`;
// Tested doing query with Apollo. Overall performance  significantly reduced.
// I im guessing its because of the size of the query.
async function getRelationshipViaDB(
  token: string | undefined,
  structureId: string,
  templateId: string | undefined
): Promise<{ data: GetStructureRelationshipsForAnalysisDataLoaderQuery | undefined }> {
  try {
    const response = await fetch(`${import.meta.env.VITE_APP_API_URL}/graphql`, {
      method: 'POST',
      headers: {
        Authorization: `bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: structureRelationshipsQueryText,
        variables: {
          structureId,
          templateId,
        },
      }),
    });

    if (response.ok) {
      const data = await response.json();
      return data;
    }
    throw new Error(`Failed to fetch structure from DB (HTTP ${response.status})`);
  } catch (error) {
    console.error('Failed to fetch structure from DB ->', error);
    return { data: undefined };
  }
}

async function getRelationshipViaS3(srPath: string) {
  try {
    const url = getCloudfrontUrl(srPath);
    const response = await fetch(url, { credentials: 'include' });
    if (response.ok) {
      const data = await response.json();
      return data;
    }
    throw new Error(`Failed to fetch structure from CDN (HTTP ${response.status})`);
  } catch (error) {
    console.error('Failed to fetch structure from CDN ->', error);
    return undefined;
  }
}
export const useSetStructureRelationship = (
  structureId: string,
  structure: GetStructureForInspectionDataLoaderQuery['structure']
) => {
  const [structureRelationship, setStructureRelationships] = useAtom(state.structureRelationship);
  const assemblyTemplate = useAtomValue(state.assemblyTemplate);
  const setFilteredAssemblyIds = useSetAtom(state.filteredAssemblyIds);
  const token = useAtomValue(auth0TokenState);

  useEffect(() => {
    const fetchData = async () => {
      let relationships: AnalysisStructureRelationships = {
        annotationIdToAssemblyId: {},
        annotationIdToAnnotation3d: {},
        annotation3dToAnnotationId: {},
        assemblyIdToAnnotation3dArray: {},
        assemblyIdAndLocationIdToAnnotation3dArray: {},
        assemblyIdToAssembly3d: {},
        partsByAnnotationId: {},
        parts: {},
      };

      const s3Path: string | undefined =
        assemblyTemplate?.structureRelationshipPath ||
        structure?.structureRelationshipPath ||
        undefined;

      try {
        if (s3Path) {
          relationships = await getRelationshipViaS3(s3Path);
        } else {
          const { data } = await getRelationshipViaDB(token, structureId, assemblyTemplate?.id);
          const allParts = data?.allParts ?? [];
          const allAssemblies = data?.allAssemblies ?? [];
          const allAnnotations = data?.allAnnotations ?? [];

          const partsById = allParts.reduce(
            (accumulator, current) => {
              accumulator[current.id] = current;
              return accumulator;
            },
            {} as {
              [key: string]: GetStructureRelationshipsForAnalysisDataLoaderQuery['allParts'][0];
            }
          );

          allAssemblies.forEach(({ id, integerId }) => {
            relationships.assemblyIdToAnnotation3dArray[id] = [];
            relationships.assemblyIdAndLocationIdToAnnotation3dArray[id] = {};
            if (integerId !== undefined && integerId !== null) {
              relationships.assemblyIdToAssembly3d[id] = integerId;
            }
          });

          allAnnotations.forEach((annotation) => {
            const { id, annotationId: annotation3dReference, partId, locationId } = annotation;

            relationships.annotationIdToAnnotation3d[id] = annotation3dReference;
            relationships.annotation3dToAnnotationId[annotation3dReference] = id;

            if (partId) {
              relationships.partsByAnnotationId[partId] =
                relationships.partsByAnnotationId[partId] ?? [];
              relationships.partsByAnnotationId[partId].push(annotation3dReference);

              relationships.parts[partId] = relationships.parts[partId] ?? {};

              if (relationships.parts[partId].annotations) {
                relationships.parts[partId].annotations.push(annotation3dReference);
              } else {
                relationships.parts[partId] = {
                  annotations: [annotation3dReference],
                  class: partsById[partId].class,
                };
              }

              const { assemblyId } = partsById[partId];
              if (assemblyId) {
                relationships.annotationIdToAssemblyId[id] = assemblyId;
                relationships.assemblyIdToAnnotation3dArray[assemblyId]?.push(
                  annotation3dReference
                );

                if (locationId) {
                  const assemblyEntry =
                    relationships.assemblyIdAndLocationIdToAnnotation3dArray[assemblyId];
                  if (assemblyEntry) {
                    if (!Object.hasOwn(assemblyEntry, locationId)) {
                      assemblyEntry[locationId] = [];
                    }
                    assemblyEntry[locationId].push(annotation3dReference);
                  } else {
                    console.error('Assembly not found by id', assemblyId);
                  }
                }
              }
            }
          });
        }

        if (relationships) {
          relationships.loaded = true;
          setStructureRelationships(relationships);
        }
      } catch (error) {
        console.error(`Error Fetching Structure Relationships - ${error}`, error);
      }
    };

    if (structureId && structure) {
      fetchData();
    }
  }, [
    structureId,
    structure,
    setStructureRelationships,
    token,
    setFilteredAssemblyIds,
    assemblyTemplate,
  ]);

  return structureRelationship;
};
